import React from 'react';

/**
 * AccountSuspended — terminal page shown to any authenticated user whose
 * database row has account_status='suspended'. This screen blocks ALL
 * application UI (dashboard, wallet, withdrawals, purchases, referrals);
 * the only available action is Logout.
 *
 * v4.7.63 Fraud Freeze — pairs with:
 *   - api/middleware/sessionAuth.js (403 ACCOUNT_SUSPENDED on protected endpoints)
 *   - supabase/migrations/20260517_fraud_freeze_enforcement.sql (DB triggers)
 *   - lib/commissions.js (skip suspended uplines in cascade)
 *
 * The wording and contact number are mandated by the operator and must not
 * be changed without explicit confirmation.
 */

interface AccountSuspendedProps {
  username?: string;
  suspensionReason?: string | null;
  suspendedAt?: string | null;
  onLogout: () => void | Promise<void>;
}

const CONTACT_NUMBER = '+27645040994';
const SUSPENSION_MESSAGE =
  'Your account has been flagged for fraud. Your account, commissions, and downline are currently frozen. Please contact the administrator immediately at +27645040994 for further instructions.';

export const AccountSuspended: React.FC<AccountSuspendedProps> = ({
  username,
  suspensionReason,
  suspendedAt,
  onLogout,
}) => {
  const formattedSuspendedAt = suspendedAt
    ? new Date(suspendedAt).toLocaleString()
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-red-500/20 rounded-full mb-6 border border-red-500/40">
            <svg
              className="w-12 h-12 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M4.93 4.93l14.14 14.14M12 2a10 10 0 100 20 10 10 0 000-20z"
              />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">Account Suspended</h1>
          {username && (
            <p className="text-gray-400 text-sm">
              Account:{' '}
              <span className="font-mono text-gray-300">{username}</span>
            </p>
          )}
        </div>

        {/* Message Card */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-red-700/40 rounded-lg p-8 shadow-2xl">
          <p className="text-gray-200 text-lg leading-relaxed mb-6">
            {SUSPENSION_MESSAGE}
          </p>

          {/* Contact callout */}
          <div className="bg-gray-900/60 border border-yellow-500/30 rounded-lg p-5 mb-6">
            <p className="text-gray-400 text-sm uppercase tracking-wider mb-2">
              Administrator Contact
            </p>
            <a
              href={`tel:${CONTACT_NUMBER}`}
              className="text-3xl font-bold text-yellow-500 hover:text-yellow-400 transition-colors block"
            >
              {CONTACT_NUMBER}
            </a>
          </div>

          {/* Frozen items */}
          <div className="bg-gray-900/40 border border-gray-700 rounded-lg p-5 mb-6">
            <p className="text-gray-300 text-sm font-semibold mb-3">
              The following are currently restricted on this account:
            </p>
            <ul className="text-gray-400 text-sm space-y-1.5">
              <li>• All withdrawal requests</li>
              <li>• Share purchases and conversions</li>
              <li>• Commission accruals (uplines + downline)</li>
              <li>• Referral and sponsor activity</li>
              <li>• Wallet and balance modifications</li>
            </ul>
          </div>

          {/* Forensic metadata (optional) */}
          {(formattedSuspendedAt || suspensionReason) && (
            <div className="text-xs text-gray-500 mb-6 border-t border-gray-700/60 pt-4 space-y-1">
              {formattedSuspendedAt && (
                <p>
                  Suspended at:{' '}
                  <span className="text-gray-400">{formattedSuspendedAt}</span>
                </p>
              )}
              {suspensionReason && (
                <p>
                  Reference:{' '}
                  <span className="text-gray-400">{suspensionReason}</span>
                </p>
              )}
            </div>
          )}

          {/* Logout */}
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => {
                void onLogout();
              }}
              className="px-8 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors border border-gray-600"
            >
              Logout
            </button>
          </div>
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">
          Aureus Africa &middot; Fraud Protection System
        </p>
      </div>
    </div>
  );
};

export default AccountSuspended;
